/**
 * This component represents a group of Segment Controls.  These controls are
 * heavily styled Radio input buttons.  The radio buttons themselves are hidden.
 * The click event on its coresponding `label` is what causes the selection to
 * occur.
 *
 * Figma:
 * https://www.figma.com/file/QtodvbP7iasbakEq4bWcpwWJ/LIHP-MAP-updates?node-id=2486%3A0
 */

import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {borderRadius} from "@repo/shared-styles";

import SegmentedControl from "./segmented-control.tsx";

export type Control = {
    /** Unique name for Radio button. */
    name: string;
    /** Copy to display in the Radio button's Label. */
    text: string;
    /** Value of the Radio button.  Passed to `onChange`. */
    value: string;
};

type Props = {
    /**
     * Optional ID for the `aria-live` section that these Segmented Controls
     * control.  Passed through to each individual `SegmentedControl`.
     */
    ariaControlsId?: string;
    /**
     * Optional ID for the label for the section that these Segmented Controls
     * control.
     */
    ariaLabelledby?: string;
    /** List of Segment Controls to render. */
    controls: Array<Control>;
    /**
     * Method to call when the Radio/Label is clicked.  It's passed the
     * value of the Radio button.
     */
    onChange: (arg1: string) => void;
    /** Used to determine which Segment Control is selected. */
    value: string;
};

const SegmentedControls = ({
    ariaControlsId,
    ariaLabelledby,
    controls,
    onChange,
    value,
}: Props): React.ReactElement => (
    <View style={styles.wrapper}>
        <View
            style={styles.container}
            aria-labelledby={ariaLabelledby}
            role="tablist"
        >
            {controls.map((control, index) => (
                <SegmentedControl
                    ariaControlsId={ariaControlsId}
                    checked={value === control.value}
                    index={index}
                    isLast={index === controls.length - 1}
                    key={index}
                    onChange={onChange}
                    {...control}
                />
            ))}
        </View>
    </View>
);

const styles = StyleSheet.create({
    wrapper: {
        alignItems: "flex-start",
        display: "inline-flex",
        marginBottom: 24,
    },
    container: {
        border: `1px solid ${color.offBlack16}`,
        borderRadius: `${borderRadius}px`,
        flexDirection: "row",
    },
});

export default SegmentedControls;
